// src/components/SellerDashboard.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
    TrendingUp,
    Users,
    ShoppingBag,
    AlertCircle,
    Megaphone,
    MousePointerClick,
    PackageCheck,
    ArrowRight,
    Rocket
} from 'lucide-react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

import dashboardBanner from '../../assets/sellerside.png'; 

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// 1. Quick Stat Card (Added Hover Border Color)
const StatCard: React.FC<{ label: string, value: string, trend: string, isPositive: boolean, icon: any }> = ({ label, value, trend, isPositive, icon: Icon }) => (
    <div className="bg-white p-5 rounded-2xl border border-gray-100 border-[#00635e]/50 shadow-sm hover:shadow-lg transition-all duration-300 group cursor-pointer relative overflow-hidden">
        {/* Subtle colored background decoration */}
        <div className="absolute top-0 right-0 w-20 h-20 bg-teal-50 rounded-full -mr-10 -mt-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        <div className="flex justify-between items-start relative z-10">
            <div>
                <p className="text-gray-500 text-sm font-medium mb-1">{label}</p>
                <h4 className="text-2xl font-black text-[#001529]">{value}</h4>
            </div>
            <div className="p-3 bg-teal-50 rounded-xl group-hover:bg-[#00635e] group-hover:text-white transition-colors text-[#00635e] shadow-sm">
                <Icon size={20} />
            </div>
        </div>
        <div className="mt-4 flex items-center text-xs font-bold relative z-10">
            <span className={`${isPositive ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'} px-2 py-1 rounded-md flex items-center`}>
                {isPositive ? '↑' : '↓'} {trend}
            </span>
            <span className="text-gray-400 ml-2">vs last month</span>
        </div>
    </div>
);

// 2. Action Card (Added Dynamic Colored Borders)
const ActionCard: React.FC<{ title: string, desc: string, btnText: string, color: string, borderColor: string, icon: any }> = ({ title, desc, btnText, color, borderColor, icon: Icon }) => (
    <div className={`bg-white rounded-2xl p-6 border-2 ${borderColor} shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden`}>
        {/* Left Strip */}
        <div className={`absolute top-0 left-0 w-1.5 h-full ${color}`}></div>
        
        <div className="flex items-center mb-4">
            <div className={`p-3 rounded-full ${color.replace('bg-', 'bg-opacity-10 text-')}`}>
                <Icon size={24} className={color.replace('bg-', 'text-')} />
            </div>
            <h3 className="ml-4 text-lg font-bold text-gray-800">{title}</h3>
        </div>
        <p className="text-gray-500 text-sm mb-6 leading-relaxed h-10">{desc}</p>
        <button className={`w-full py-2.5 rounded-xl font-bold text-sm border-2 transition-all flex items-center justify-center gap-2
            ${color === 'bg-[#00635e]' ? 'border-[#00635e] text-[#00635e] hover:bg-[#00635e] hover:text-white' : ''}
            ${color === 'bg-red-600' ? 'border-red-600 text-red-600 hover:bg-red-600 hover:text-white' : ''}
            ${color === 'bg-orange-500' ? 'border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white' : ''}
        `}>
            {btnText} <ArrowRight size={16} />
        </button>
    </div>
);

// --- Main Dashboard Component ---
const SellerDashboard: React.FC = () => {
    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-10 pt-2 px-4 md:px-0">
            
            {/* --- Banner Section --- */}
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-r from-[#00635e] to-[#004d49] text-white shadow-xl flex flex-col md:flex-row items-center justify-between p-6  md:py-0 md:h-[300px]">
                <div className="relative z-10 w-full md:w-1/2 space-y-5 flex flex-col items-center md:items-start text-center md:text-left order-2 md:order-1 py-6 md:py-0">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-xs font-bold backdrop-blur-md border border-white/10 text-teal-100 shadow-sm">
                        <Rocket size={14} className="text-red-400" />
                        Boost Your Sales Engine
                    </div>
                    <h1 className="text-1xl md:text-2xl lg:text-4xl font-black leading-tight tracking-tight">
                        Ready to scale your <br className="hidden md:block" /> business, <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-teal-200">Nilesh?</span>
                    </h1>
                    <p className="text-teal-100 text-sm md:text-base font-medium max-w-xl leading-relaxed hidden md:block">
                        Track performance, manage inventory, and respond to inquiries all in one place.
                    </p>
                    <div className="flex flex-wrap gap-3 pt-2 justify-center md:justify-start">
                        <Link to="/seller/add-product" className="bg-red-600 hover:bg-red-500 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-red-900/30 transition-all flex items-center gap-2 text-sm active:scale-95">
                            <TrendingUp size={18} /> Post Free Product
                        </Link>
                        <button className="bg-white text-[#00635e] hover:bg-teal-50 px-6 py-3 rounded-xl font-bold shadow-lg transition-all text-sm active:scale-95 hidden md:flex">
                            View Analytics
                        </button>
                    </div>
                </div>

                <div className="relative z-10 w-full md:w-1/2 h-[220px] md:h-full flex justify-center items-end md:items-center order-1 md:order-2 pb-0 md:pb-0">
                    <img
                        src={dashboardBanner} 
                        alt="Business Growth Illustration"
                        className="h-full w-full object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-500 animate-in fade-in slide-in-from-right-10 origin-bottom"
                    />
                </div>
                <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] mix-blend-overlay pointer-events-none"></div>
            </div>

            {/* --- Quick Stats Row --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Total Revenue" value="₹45,231" trend="12.5%" isPositive={true} icon={TrendingUp} />
                <StatCard label="Active Orders" value="24" trend="8.2%" isPositive={true} icon={ShoppingBag} />
                <StatCard label="Profile Views" value="1,204" trend="3.1%" isPositive={false} icon={Users} />
                <StatCard label="Pending Inquiries" value="08" trend="Urgent" isPositive={true} icon={AlertCircle} />
            </div>

            {/* --- Action Cards (With Explicit Border Colors) --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <ActionCard
                    title="Advertising"
                    desc="Boost visibility by 5x using sponsored listings."
                    btnText="Start Campaign"
                    color="bg-[#00635e]"
                    borderColor="border-[#00635e]/30" // Teal Border
                    icon={Megaphone}
                />
                <ActionCard
                    title="Inquiries"
                    desc="You have 15 unread messages from buyers."
                    btnText="Check Inbox"
                    color="bg-red-600"
                    borderColor="border-red-200" // Red Border
                    icon={MousePointerClick}
                />
                <ActionCard
                    title="Inventory"
                    desc="3 Products are low on stock. Update now."
                    btnText="Update Stock"
                    color="bg-orange-500"
                    borderColor="border-orange-200" // Orange Border
                    icon={PackageCheck}
                />
            </div>

            {/* --- Main Content Grid --- */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Chart Section (With Top Border Accent) */}
                <div className="lg:col-span-2 bg-white p-6 md:p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-[#00635e]"></div> {/* Top Accent Line */}
                    
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                        <div>
                            <h2 className="text-xl font-bold text-[#001529]">Sales Overview</h2>
                            <p className="text-gray-400 text-sm">Performance over the last 7 days</p>
                        </div>
                        <select className="bg-gray-50 border-none text-sm font-bold text-gray-600 rounded-lg p-2 focus:ring-0 cursor-pointer outline-none hover:bg-gray-100 transition-colors">
                            <option>Last 7 Days</option>
                            <option>Last Month</option>
                        </select>
                    </div>
                    <div className="h-[300px] w-full">
                        <Line
                            data={{
                                labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
                                datasets: [{
                                    label: "Store Visits",
                                    data: [400, 600, 550, 900, 700, 1100, 1300],
                                    borderColor: "#00635e",
                                    borderWidth: 3,
                                    pointRadius: 4,
                                    pointBackgroundColor: "#fff",
                                    pointBorderColor: "#00635e",
                                    tension: 0.4,
                                    fill: true,
                                    backgroundColor: (context) => {
                                        const ctx = context.chart.ctx;
                                        const gradient = ctx.createLinearGradient(0, 0, 0, 300);
                                        gradient.addColorStop(0, "rgba(0, 99, 94, 0.2)");
                                        gradient.addColorStop(1, "rgba(0, 99, 94, 0)");
                                        return gradient;
                                    }
                                }]
                            }}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: { legend: { display: false } },
                                scales: {
                                    y: { grid: { display: true, color: '#f3f4f6' }, border: { display: false }, ticks: { font: { weight: 'bold' } } },
                                    x: { grid: { display: false }, border: { display: false }, ticks: { font: { weight: 'bold' } } }
                                }
                            }}
                        />
                    </div>
                </div>

                {/* Seller Health - Dark Card */}
                <div className="bg-[#001529] rounded-3xl p-6 md:p-8 text-white shadow-xl relative overflow-hidden flex flex-col justify-between min-h-[350px]">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500 rounded-full blur-[60px] opacity-20"></div>

                    <div>
                        <h3 className="text-xl font-bold mb-1">Seller Health</h3>
                        <p className="text-gray-400 text-xs mb-6">Maintain good health for better reach.</p>

                        <div className="space-y-6 relative z-10">
                            {[
                                { label: "Response Rate", val: "98%", color: "text-green-400" },
                                { label: "Order Defect Rate", val: "0.5%", color: "text-green-400" },
                                { label: "Late Dispatch", val: "2.0%", color: "text-yellow-400" }
                            ].map((item, idx) => (
                                <div key={idx} className="flex justify-between items-center border-b border-white/10 pb-3 last:border-0">
                                    <span className="text-gray-400 text-sm">{item.label}</span>
                                    <span className={`${item.color} font-bold`}>{item.val}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-8 p-4 bg-white/5 rounded-2xl backdrop-blur-sm border border-white/10 shadow-inner">
                        <div className="flex justify-between items-end mb-2">
                            <span className="text-xs font-bold text-gray-300 uppercase">Profile Score</span>
                            <span className="text-xl font-black text-white">85%</span>
                        </div>
                        <div className="w-full bg-gray-700 h-2 rounded-full overflow-hidden">
                            <div className="bg-gradient-to-r from-red-500 to-orange-500 h-full w-[85%] rounded-full shadow-[0_0_10px_rgba(239,68,68,0.5)]"></div>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-2 font-medium">Add GSTIN to reach 100%</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SellerDashboard;